import * as React from "react"
import { useCallback, useContext, useState } from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@/lib/utils"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Button } from "@/components/ui/button"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import {
  CarFront,
  Check,
  CheckCheck,
  Clock,
  Copy,
  Forward,
  MoreHorizontal,
  Pin,
  Reply,
  Smile,
  Search,
  Trash2,
  Unlink2,
} from "lucide-react"
import { type Message as MessageType } from "@/reducers/messagesSlice"
import { format } from "date-fns"
import { useTranslation } from "react-i18next"
import { getDateFnsLocale } from "@/lib/i18n/format"
import { formatMessageTimestamp } from "@/lib/messageDate"
import { SparkleIconActive } from "@/components/icons/SparkleIcon"
import { CircularAlertFillIcon } from "@/components/icons/CircularAlertFillIcon"
import { MessageStatus } from "@/lib/messageHelpers"
import { ReactionSelector } from "@/components/ui/message/reaction-picker"
import { useMessageData } from "@/components/chat/useMessageData"
import { type PinDuration } from "@/components/chat/useMessageActions"
import DeleteMessageDialog from "@/components/chat/DeleteMessageDialog"
import { isMobile } from "@/utils/detectDevice"
import { useShowAITouchedBadge } from "@/components/chat/useShowAITouchedBadge"
import SlickAITouchedBadge from "@/components/chat/SlickAITouchedBadge"
import { useAppSelector } from "@/reducers/hooks"
import { selectSettings } from "@/reducers/settingsSlice"
import SettingsApi from "@/api/settingsApi"
import { captureError } from "@/ErrorHandlers"
import { XmppContext } from "@/stream/xmppClient"
import useImageCache from "@/hooks/useImageCache"
import { MessageBody } from "@/components/ui/message/message-body"
import { useMessageSwipeWithNavigation } from "@/hooks/useMessageSwipeWithNavigation"
import { mobileRoutes, updateMobileRoute } from "@/routes/routes"
import { useIsDriverWorkspace } from "@/contexts/DriverWorkspaceContext"
import { MESSAGE_ROW_INSET } from "@/components/ui/message/rowInset"

/**
 * Markdown rendered inside a bubble. Deliberately not `AI_CHAT_PROSE_CLASS`:
 * that is the Slick AI column's own typography (`prose-base`,
 * `text-foreground`, airy paragraph margins), right for a full-width pane and
 * a size too large for a bubble sitting next to plain `text-sm` text from the
 * dispatcher. Lives here, next to `bubbleVariants`, because what markdown
 * looks like inside a bubble is the bubble's business.
 *
 * Colour is inherited rather than named, so the bubble variant keeps owning it
 * — name it here and a bubble variant that sets a different text colour (`ai`
 * sets none at all) would be silently overridden.
 */
const bubbleProseClass =
  "prose prose-sm max-w-none leading-normal dark:prose-invert prose-p:text-inherit prose-headings:text-inherit prose-headings:text-sm prose-table:my-0 [&_p]:!my-0 [&_p+p]:!mt-2"

// Message root component variants
const messageVariants = cva("group relative flex gap-2 mb-6 mx-4", {
  variants: {
    variant: {
      incoming: "flex-row",
      outgoing: "flex-row-reverse",
      ai: "flex-row",
    },
    grouped: {
      true: "mb-1",
      false: "mb-4",
    },
  },
  defaultVariants: {
    variant: "incoming",
    grouped: false,
  },
})

// Message bubble variants
const bubbleVariants = cva(
  "relative max-w-[calc(100%-72px)] min-w-0 break-words rounded-lg p-2 transition-colors",
  {
    variants: {
      variant: {
        incoming: "bg-secondary text-accent-foreground rounded-bl-none",
        outgoing: "bg-secondary text-accent-foreground rounded-br-none",
        ai: "bg-violet-100 dark:bg-violet-950 rounded-lg",
      },
      highlighted: {
        true: "animate-pulse bg-accent",
        false: "",
      },
      withDriver: {
        true: "bg-blue-100 dark:bg-blue-950",
        false: "",
      },
    },
    defaultVariants: {
      variant: "incoming",
      highlighted: false,
      withDriver: false,
    },
  },
)

// Message container context
interface MessageContextValue {
  message: MessageType
  replyMessage?: MessageType
  variant: "incoming" | "outgoing" | "ai" | null | undefined
  grouped: boolean | null
  showAvatar: boolean
  onReply?: () => void
  onForward?: () => void
  onDelete?: () => Promise<void>
  onReact?: (emoji: string | null) => Promise<void>
  onPin?: (duration: PinDuration) => Promise<void>
  onUnPin?: () => Promise<void>
  onCopy?: () => void
  onUnlinkDocument?: () => Promise<void>
  onFindDeliveryOrder?: () => void
  interactive?: boolean
  actionButtonsVisible?: boolean
  hideActionButtons?: () => void
}

const MessageContext = React.createContext<MessageContextValue | null>(null)

export const useMessageContext = () => {
  const context = React.useContext(MessageContext)
  if (!context) {
    throw new Error("Message compound components must be used within Message")
  }
  return context
}

// Props interfaces
export interface MessageProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof messageVariants> {
  message?: MessageType
  replyMessage?: MessageType
  showAvatar?: boolean
  onReply?: () => void
  onForward?: () => void
  onDelete?: () => Promise<void>
  onReact?: (emoji: string | null) => Promise<void>
  onPin?: (duration: PinDuration) => Promise<void>
  onUnPin?: () => Promise<void>
  onCopy?: () => void
  onUnlinkDocument?: () => Promise<void>
  onFindDeliveryOrder?: () => void
  interactive?: boolean
  actionButtonsVisible?: boolean
  hideActionButtons?: () => void
}

export interface MessageBubbleProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof bubbleVariants> {}

export interface MessageAvatarProps
  extends React.HTMLAttributes<HTMLDivElement> {
  showWhenReadOnly?: boolean
  src?: string
  fallback?: string
  avatarColor?: string
  name?: string
}

export interface MessageHeaderProps
  extends React.HTMLAttributes<HTMLDivElement> {
  author?: string
  timestamp?: string | Date
  showAuthor?: boolean
  showTimestamp?: boolean
  /** Keep the timestamp invisible until the message is hovered (same
   * group-hover the action buttons use), instead of always showing it with
   * a hover tooltip for the full date. */
  hideTimestampUntilHover?: boolean
}

export interface MessageStatusProps
  extends React.HTMLAttributes<HTMLDivElement> {
  status?: MessageStatus | undefined
  isFromDriver?: boolean
  showStatus?: boolean
}

export interface MessageActionsProps
  extends React.HTMLAttributes<HTMLDivElement> {
  showOnHover?: boolean
  actions?: {
    reply?: boolean
    forward?: boolean
    delete?: boolean
    pin?: boolean
    unPin?: boolean
    copy?: boolean
    react?: boolean
  }
}

export interface MessageReactionsProps
  extends React.HTMLAttributes<HTMLDivElement> {
  readOnly?: boolean
  reactions?: Array<{
    emoji: string
    count: number
    isOwnReaction?: boolean
  }>
  onReactionClick?: (emoji: string) => void
}

// Root Message component
const Message = ({
  className,
  // @ts-ignore
  message = {},
  replyMessage,
  variant = "incoming",
  grouped = false,
  showAvatar = true,
  onReply,
  onForward,
  onDelete,
  onReact,
  onPin,
  onUnPin,
  onCopy,
  onUnlinkDocument,
  onFindDeliveryOrder,
  children,
  interactive = true,
  actionButtonsVisible,
  hideActionButtons,
  ...props
}: MessageProps) => {
  const contextValue = React.useMemo<MessageContextValue>(
    () => ({
      // @ts-ignore
      message,
      replyMessage,
      variant,
      grouped,
      showAvatar,
      onReply,
      onForward,
      onDelete,
      onReact,
      onPin,
      onUnPin,
      onCopy,
      onUnlinkDocument,
      onFindDeliveryOrder,
      interactive,
      actionButtonsVisible,
      hideActionButtons,
    }),
    [
      message,
      replyMessage,
      variant,
      grouped,
      showAvatar,
      onReply,
      onForward,
      onDelete,
      onReact,
      onPin,
      onUnPin,
      onCopy,
      onUnlinkDocument,
      onFindDeliveryOrder,
      interactive,
      actionButtonsVisible,
      hideActionButtons,
    ],
  )

  const handleNavigateBack = () => {
    updateMobileRoute(mobileRoutes.CHAT_LIST)
  }

  const inWorkspace = useIsDriverWorkspace()

  const messageSwipe = useMessageSwipeWithNavigation({
    onReply: onReply ? onReply : () => {},
    onNavigateBack: handleNavigateBack,
  })

  return (
    <MessageContext.Provider value={contextValue}>
      <div
        {...(isMobile && interactive ? messageSwipe.handlers : {})}
        ref={isMobile && interactive ? messageSwipe.ref : undefined}
        data-testid="message-row"
        className={cn(
          messageVariants({
            variant,
            grouped,
          }),
          // The row decides its own inset from the layout it is in. Leaving
          // this to each message component is how the dispatcher's bubble and
          // Slick AI's ended up at different widths: two callers, two
          // conditions, one of them also waiting on the participant list.
          inWorkspace && MESSAGE_ROW_INSET,
          className,
        )}
        {...props}
      >
        {children}
      </div>
    </MessageContext.Provider>
  )
}

// Message Avatar component
const MessageAvatar = React.forwardRef<HTMLDivElement, MessageAvatarProps>(
  (
    {
      className,
      avatarColor,
      src,
      fallback,
      name,
      showWhenReadOnly = false,
      ...props
    },
    ref,
  ) => {
    const { showAvatar, interactive } = useMessageContext()
    const { cachedSrc } = useImageCache(src)
    if (!interactive && !showWhenReadOnly) return null
    return (
      <div ref={ref} className={cn("min-w-8 self-end", className)} {...props}>
        {showAvatar && (
          <Avatar className="h-7 w-7">
            <AvatarImage src={cachedSrc} alt={name} />
            <AvatarFallback className={cn("text-xs", avatarColor)}>
              {fallback}
            </AvatarFallback>
          </Avatar>
        )}
      </div>
    )
  },
)
MessageAvatar.displayName = "MessageAvatar"

// Message Bubble wrapper
const MessageBubble = React.forwardRef<HTMLDivElement, MessageBubbleProps>(
  (
    {
      className,
      variant: bubbleVariant,
      highlighted,
      withDriver,
      children,
      ...props
    },
    ref,
  ) => {
    const { variant } = useMessageContext()

    return (
      <div
        ref={ref}
        className={cn(
          bubbleVariants({
            variant: bubbleVariant || variant,
            highlighted,
            withDriver,
          }),
          className,
        )}
        {...props}
      >
        {children}
      </div>
    )
  },
)
MessageBubble.displayName = "MessageBubble"

// Message Header (author + timestamp)
const MessageHeader = React.forwardRef<HTMLDivElement, MessageHeaderProps>(
  (
    {
      className,
      author,
      timestamp,
      showAuthor = true,
      showTimestamp = true,
      hideTimestampUntilHover = false,
      children,
      ...props
    },
    ref,
  ) => {
    const { message } = useMessageContext()
    const { t } = useTranslation()

    const displayAuthor =
      author || (message?.from ? t("chat.message.defaultAuthor") : "")
    const displayTimestamp = timestamp || message?.timestamp

    return (
      <div
        ref={ref}
        className={cn(
          "mb-0.5 flex items-center justify-between gap-2 rounded-full px-0 text-sm font-medium",
          !showAuthor && "justify-end",
          className,
        )}
        {...props}
      >
        {showAuthor && <span className="flex-1">{displayAuthor}</span>}
        {showTimestamp &&
          displayTimestamp &&
          (hideTimestampUntilHover ? (
            <span className="shrink-0 whitespace-nowrap text-xs text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100">
              {typeof displayTimestamp === "string"
                ? displayTimestamp
                : formatMessageTimestamp(new Date(displayTimestamp))}
            </span>
          ) : (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="shrink-0 whitespace-nowrap text-xs text-muted-foreground">
                    {typeof displayTimestamp === "string"
                      ? displayTimestamp
                      : formatMessageTimestamp(new Date(displayTimestamp))}
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  <p>
                    {typeof displayTimestamp === "string"
                      ? displayTimestamp
                      : format(new Date(displayTimestamp), "EEEE, PPP HH:mm", {
                          locale: getDateFnsLocale(),
                        })}
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ))}
        {children}
      </div>
    )
  },
)
MessageHeader.displayName = "MessageHeader"

// Message Status component
const MessageStatus = React.forwardRef<HTMLDivElement, MessageStatusProps>(
  (
    {
      className,
      status,
      showStatus = true,
      isFromDriver = false,
      children,
      ...props
    },
    ref,
  ) => {
    const { t } = useTranslation()
    if (!showStatus || isFromDriver) return null

    const getStatusIcon = () => {
      if (isFromDriver)
        return status !== "sent" ? (
          <CheckCheck
            className={cn(
              "size-3.5",
              status === "displayed" && "text-blue-500",
            )}
          />
        ) : (
          <Check className="size-3.5" />
        )

      switch (status) {
        case "scheduled":
          return <Clock className="size-3.5" />
        case "sent":
          return <Check className="size-3.5" />
        case "received":
          return <CarFront className="size-3.5" />
        case "displayed":
          return <CarFront className="size-3.5 text-blue-500" />
        case "not_sent":
          return <CircularAlertFillIcon className="size-3.5 text-destructive" />
        default:
          return null
      }
    }

    const getTooltipText = () => {
      switch (status) {
        case "scheduled":
          return t("chat.message.status.scheduled")
        case "sent":
          return t("chat.message.status.sent")
        case "received":
          return t("chat.message.status.received")
        case "displayed":
          return t("chat.message.status.displayed")
        case "not_sent":
          return t("chat.message.status.notSent")
        default:
          return ""
      }
    }
    const statusIcon = getStatusIcon()
    const tooltipText = getTooltipText()

    return (
      <div
        ref={ref}
        className={cn("text-muted-foreground", className)}
        {...props}
      >
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="cursor-default">{statusIcon}</div>
            </TooltipTrigger>
            <TooltipContent>
              <p>{tooltipText}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        {children}
      </div>
    )
  },
)
MessageStatus.displayName = "MessageStatus"

// Message Actions (hover menu)
const MessageActions = React.forwardRef<HTMLDivElement, MessageActionsProps>(
  ({ className, showOnHover = true, children, ...props }, ref) => {
    const {
      onReply,
      onForward,
      onDelete,
      onPin,
      onUnPin,
      onCopy,
      onReact,
      onUnlinkDocument,
      onFindDeliveryOrder,
      message,
      variant,
      actionButtonsVisible,
      hideActionButtons,
    } = useMessageContext()
    const { t } = useTranslation()
    const { reactions } = useMessageData(message)
    const [isActionMenuOpen, setIsActionMenuOpen] = useState(false)
    const [isReactionMenuOpen, setIsReactionMenuOpen] = useState(false)
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)

    const selectedReaction =
      reactions.find((r) => r.isOwnReaction)?.emoji || undefined

    const handleReact = useCallback(
      (emoji: string | null) => {
        onReact?.(emoji)
        setIsReactionMenuOpen(false)
        // On mobile, hide action buttons after a delay
        if (isMobile && hideActionButtons) {
          setTimeout(() => {
            hideActionButtons()
          }, 100)
        }
      },
      [onReact, hideActionButtons],
    )

    const showActionMenu =
      onDelete ||
      onPin ||
      onUnPin ||
      onUnlinkDocument ||
      onFindDeliveryOrder ||
      onForward

    if (
      isMobile &&
      !actionButtonsVisible &&
      !isActionMenuOpen &&
      !isReactionMenuOpen &&
      !isDeleteDialogOpen
    )
      return null

    return (
      <>
        {isDeleteDialogOpen && onDelete && (
          <DeleteMessageDialog
            open={isDeleteDialogOpen}
            message={message}
            onDelete={onDelete}
            onClose={() => {
              setIsDeleteDialogOpen(false)
              // On mobile, hide action buttons after delete dialog closes
              if (isMobile && hideActionButtons) {
                setTimeout(() => {
                  hideActionButtons()
                }, 100)
              }
            }}
          />
        )}
        <div
          ref={ref}
          className={cn(
            "z-10 flex gap-1",
            // Desktop positioning - show on hover
            showOnHover &&
              !isMobile &&
              !isActionMenuOpen &&
              !isReactionMenuOpen &&
              "opacity-0 transition-opacity group-hover:opacity-100",
            // Mobile positioning - use shifted space
            isMobile &&
              !actionButtonsVisible &&
              !isActionMenuOpen &&
              !isReactionMenuOpen &&
              !isDeleteDialogOpen &&
              "pointer-events-none opacity-0",
            variant === "incoming" && "flex-row-reverse",
            // For outgoing messages on mobile, align to start of shifted space
            className,
          )}
          {...props}
        >
          {showActionMenu && (
            <DropdownMenu
              onOpenChange={(open) => {
                setIsActionMenuOpen(open)
                // When action menu closes on mobile, hide action buttons after a delay
                if (!open && isMobile && hideActionButtons) {
                  setTimeout(() => {
                    hideActionButtons()
                  }, 100)
                }
              }}
            >
              <DropdownMenuTrigger asChild>
                <Button
                  data-testid="more-menu"
                  variant="ghost"
                  size="icon-sm"
                  className="h-8 w-7 border border-input bg-background shadow-xs"
                >
                  <MoreHorizontal className="h-3 w-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-52">
                {onForward && (
                  <DropdownMenuItem onClick={onForward}>
                    <Forward className="mr-2 h-4 w-4" />
                    {t("chat.message.actions.forward")}
                  </DropdownMenuItem>
                )}
                {onCopy && (
                  <DropdownMenuItem onClick={onCopy}>
                    <Copy className="mr-2 h-4 w-4" />
                    {t("chat.message.actions.copy")}
                  </DropdownMenuItem>
                )}
                {onPin && (
                  <>
                    <DropdownMenuItem
                      onClick={() => onPin("48h")}
                      data-testid="pin-message"
                    >
                      <Pin className="mr-2 h-4 w-4" />
                      {t("chat.message.actions.pin48h")}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => onPin("30d")}
                      data-testid="pin-message-30d"
                    >
                      <Pin className="mr-2 h-4 w-4" />
                      {t("chat.message.actions.pin30d")}
                    </DropdownMenuItem>
                  </>
                )}
                {onUnPin && (
                  <DropdownMenuItem
                    onClick={onUnPin}
                    date-testid="unpin-message"
                  >
                    <Pin className="mr-2 h-4 w-4" />
                    {t("chat.message.actions.unpin")}
                  </DropdownMenuItem>
                )}{" "}
                {onUnlinkDocument && (
                  <DropdownMenuItem onClick={onUnlinkDocument}>
                    <Unlink2 className="mr-2 h-4 w-4" />
                    {t("chat.message.actions.unlinkDeliveryOrder")}
                  </DropdownMenuItem>
                )}
                {onFindDeliveryOrder && (
                  <DropdownMenuItem onClick={onFindDeliveryOrder}>
                    <Search className="mr-2 h-4 w-4" />
                    {t("chat.message.actions.findDeliveryOrder")}
                  </DropdownMenuItem>
                )}
                {onDelete && (
                  <>
                    <DropdownMenuItem
                      onClick={() => setIsDeleteDialogOpen(true)}
                      className="text-destructive"
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      {t("common.delete")}
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          {onReply && (
            <Button
              data-testid="reply"
              onClick={onReply}
              variant="ghost"
              size="icon-sm"
              className="h-8 w-7 border border-input bg-background shadow-xs"
            >
              <Reply className="h-3 w-3" />
            </Button>
          )}
          {onReact && (
            <DropdownMenu
              modal={false}
              open={isReactionMenuOpen}
              onOpenChange={(open) => {
                setIsReactionMenuOpen(open)
                // When reaction menu closes on mobile, hide action buttons after a delay
                if (!open && isMobile && hideActionButtons) {
                  setTimeout(() => {
                    hideActionButtons()
                  }, 100)
                }
              }}
            >
              <DropdownMenuTrigger asChild>
                <Button
                  data-testid="reaction-picker"
                  variant="ghost"
                  size="icon-sm"
                  className="h-8 w-7 border border-input bg-background shadow-xs"
                >
                  <Smile className="h-3 w-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align={variant === "outgoing" ? "end" : "start"}
                side="top"
                className="p-0 shadow-xs"
              >
                <DropdownMenuItem asChild>
                  <ReactionSelector
                    selectedReaction={selectedReaction}
                    onReactionSelect={handleReact}
                  />
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          {children}
        </div>
      </>
    )
  },
)
MessageActions.displayName = "MessageActions"

// Message Reactions component
const MessageReactions = React.forwardRef<
  HTMLDivElement,
  MessageReactionsProps
>(({ className, reactions, readOnly = false, children, ...props }, ref) => {
  const { interactive, variant } = useMessageContext()
  if (!reactions?.length || (!interactive && !readOnly)) return null

  const emojis = Array.from(new Set<string>([...reactions.map((r) => r.emoji)]))
  const hasOwnReaction = reactions.some((r) => r.isOwnReaction)
  if (emojis.length === 0) return null
  return (
    <div
      ref={ref}
      className={cn(
        "absolute bottom-[-18px] right-2 z-50 flex flex-wrap gap-1",
        variant === "incoming" && "left-2",
        className,
      )}
      {...props}
    >
      <div
        className={cn(
          "flex h-6 items-center justify-center gap-1 rounded-full border px-1 text-sm shadow-xs",
          hasOwnReaction
            ? "border-active-chat/20 bg-[#EEEDFF] dark:border-active-chat/30 dark:bg-[#262548]"
            : "border-background bg-secondary",
        )}
      >
        {emojis.map((emoji, index) => (
          <span key={index}>{emoji}</span>
        ))}
        {reactions.length > 1 && <span>{reactions.length}</span>}
      </div>
      {children}
    </div>
  )
})
MessageReactions.displayName = "MessageReactions"

// Message Metadata component (AI badges, translations)
const MessageMetadata = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, children, ...props }, ref) => {
  const { variant, message, interactive } = useMessageContext()
  const { t } = useTranslation()
  const { client } = useContext(XmppContext)
  const settings = useAppSelector(selectSettings)

  const handleToggleTranslations = async () => {
    try {
      await SettingsApi.setShowTranslations(client, !settings.showTranslations)
      window.analytics?.track("ToggleTranslations", {
        to: settings.showTranslations,
      })
    } catch (error) {
      captureError(error, {
        origin: "UserAction",
        extra: { message: "setShowTranslations" },
      })
    }
  }

  const {
    hasRouteUnchanged,
    deliveryOrder,
    routeUpdates,
    showAITouchedBadge,
    transcription,
    translation,
    linkedFiles,
    linkType,
  } = useShowAITouchedBadge(message)

  if (!children && !showAITouchedBadge) return null

  return (
    <div
      ref={ref}
      className={cn(
        "mt-2 flex items-center gap-2 text-xs",
        variant === "outgoing" ? "justify-end" : "justify-start",
        className,
      )}
      {...props}
    >
      {showAITouchedBadge && (
        // Outer row: one flex line holding the badge and, for translations,
        // the "Show translation" button pinned to its right.
        <div className="flex w-full min-w-0 items-start gap-2 text-violet-600 dark:text-violet-400">
          {/* Text flow, not a two-column layout — see AttachmentLine in SlickAITouchedBadge.tsx for why. */}
          <div className="min-w-0 flex-1 [overflow-wrap:anywhere]">
            <span className="whitespace-nowrap">
              <SparkleIconActive className="mr-1 inline-block size-3 align-[-0.125em]" />
              Slick AI
            </span>{" "}
            <SlickAITouchedBadge
              message={message}
              routeUpdates={routeUpdates}
              hasRouteUnchanged={hasRouteUnchanged}
              translation={Boolean(translation)}
              transcription={transcription}
              deliveryOrder={deliveryOrder}
              linkedFiles={linkedFiles}
              linkType={linkType}
            />
          </div>
          {translation && (
            <div className="flex shrink-0 items-end justify-end">
              <Button
                variant="link"
                size="sm"
                className="h-auto p-0 text-xs text-blue-600 no-underline hover:text-blue-800 hover:no-underline"
                disabled={!interactive}
                onClick={handleToggleTranslations}
              >
                {settings.showTranslations
                  ? t("chat.message.hideTranslation")
                  : t("chat.message.showTranslation")}
              </Button>
            </div>
          )}
        </div>
      )}
      {children}
    </div>
  )
})
MessageMetadata.displayName = "MessageMetadata"

/**
 * Markdown body of a bubble, at the bubble's typography. The surface decides
 * the typography, so a renderer can be dropped in here without carrying a
 * class name through its own call signature.
 */
const MessageMarkdown = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, children, ...props }, ref) => (
  <div
    ref={ref}
    data-testid="message-markdown"
    className={cn(bubbleProseClass, className)}
    {...props}
  >
    {children}
  </div>
))
MessageMarkdown.displayName = "MessageMarkdown"

// Message Content wrapper (for different content types)
const MessageContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, children, ...props }, ref) => {
  return (
    <div ref={ref} className={cn("text-sm", className)} {...props}>
      {children}
    </div>
  )
})
MessageContent.displayName = "MessageContent"

// Create compound component with proper TypeScript typing
interface MessageComponent
  extends React.ForwardRefExoticComponent<
    MessageProps & React.RefAttributes<HTMLDivElement>
  > {
  Avatar: typeof MessageAvatar
  Bubble: typeof MessageBubble
  Header: typeof MessageHeader
  Content: typeof MessageContent
  Body: typeof MessageBody
  Status: typeof MessageStatus
  Markdown: typeof MessageMarkdown
  Actions: typeof MessageActions
  Reactions: typeof MessageReactions
  Metadata: typeof MessageMetadata
}

// Assign compound components to Message
const CompoundMessage = Message as MessageComponent
CompoundMessage.Avatar = MessageAvatar
CompoundMessage.Bubble = MessageBubble
CompoundMessage.Header = MessageHeader
CompoundMessage.Content = MessageContent
CompoundMessage.Body = MessageBody
CompoundMessage.Status = MessageStatus
CompoundMessage.Markdown = MessageMarkdown
CompoundMessage.Actions = MessageActions
CompoundMessage.Reactions = MessageReactions
CompoundMessage.Metadata = MessageMetadata

// Export all components
export {
  CompoundMessage as Message,
  MessageAvatar,
  MessageBubble,
  MessageHeader,
  MessageContent,
  MessageStatus,
  MessageMarkdown,
  MessageActions,
  MessageReactions,
  MessageMetadata,
  MessageBody,
}
